/**
 * CrossMatterChangesListPage - Paginated list of matters with pending CMS changes
 *
 * Uses generic DataTable with server-side pagination, sorting, and filtering.
 * Follows the Matters page pattern.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '@/components/common/DataTable';
import type { TableColumn } from '@/components/common/DataTable/types';
import type { BadgeColor } from '@/components/ui/catalyst/badge';
import { Badge } from '@/components/ui/catalyst/badge';
import { usePagination } from '@/hooks/usePagination';
import {
  type CrossMatterChangesFilterParams,
  type EntityCategory,
  type MatterChangeSummarySchema,
  useGetCrossMatterChangesFilterOptionsQuery,
  useGetCrossMatterChangesListQuery,
} from '@/services/api/crossMatterChangesService';
import { getResults, getTotalCount, toQueryParams } from '@/utils/pagination';

/* ------------------------------------------------------------------ */
/*  Category display config                                           */
/* ------------------------------------------------------------------ */

const CATEGORY_DISPLAY: Record<EntityCategory, { label: string; color: BadgeColor }> = {
  PROVIDER: { label: 'Providers', color: 'purple' },
  TREATMENT_REQUEST: { label: 'TRRs', color: 'blue' },
  COVERAGE: { label: 'Coverages', color: 'amber' },
  TASK: { label: 'Tasks', color: 'green' },
  TASK_UPDATE: { label: 'Task Updates', color: 'cyan' },
  STAFF_NOTE: { label: 'Notes', color: 'pink' },
  DOCUMENT: { label: 'Documents', color: 'sky' },
  OTHER: { label: 'Other', color: 'zinc' },
};

/* ------------------------------------------------------------------ */
/*  Filter key mapping (column id → backend key)                      */
/* ------------------------------------------------------------------ */

const FILTER_KEY_MAP: Record<string, string> = {
  firm_name: 'firm',
  matter_name: 'matter_id',
};

function getBackendFilterKey(columnId: string): string {
  return FILTER_KEY_MAP[columnId] ?? columnId;
}

function getColumnIdFromBackend(backendKey: string): string {
  const entry = Object.entries(FILTER_KEY_MAP).find(([, v]) => v === backendKey);
  return entry ? entry[0] : backendKey;
}

/* ------------------------------------------------------------------ */
/*  Sort key mapping (column id → backend ordering field)             */
/* ------------------------------------------------------------------ */

const SORT_KEY_MAP: Record<string, string> = {
  matter_name: 'name',
  firm_name: 'firm__name',
};

function getOrderingField(columnId: string): string {
  return SORT_KEY_MAP[columnId] ?? columnId;
}

/* ------------------------------------------------------------------ */
/*  Relative date formatter                                           */
/* ------------------------------------------------------------------ */

function formatRelativeDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/* ------------------------------------------------------------------ */
/*  Cell renderers (defined outside component to avoid lint errors)   */
/* ------------------------------------------------------------------ */

function renderMatterCell(row: MatterChangeSummarySchema) {
  return (
    <div>
      <div className="font-medium text-gray-900 dark:text-white">
        {row.matter_name}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {row.firm_name}
      </div>
    </div>
  );
}

function renderPendingCell(row: MatterChangeSummarySchema) {
  return (
    <Badge color="red" className="text-xs">
      {row.total_events}
    </Badge>
  );
}

function renderCategoriesCell(row: MatterChangeSummarySchema) {
  return (
    <div className="flex flex-wrap gap-1">
      {row.category_counts
        .filter((c) => c.count > 0)
        .map((c) => {
          const display = CATEGORY_DISPLAY[c.category] ?? {
            label: c.category,
            color: 'zinc' as BadgeColor,
          };
          return (
            <Badge
              key={c.category}
              color={display.color}
              className="text-xs"
            >
              {display.label} {c.count}
            </Badge>
          );
        })}
    </div>
  );
}

function renderLatestEventCell(row: MatterChangeSummarySchema) {
  return (
    <span className="text-sm text-gray-500">
      {formatRelativeDate(row.latest_event_at)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Column factory (outside component, like createMatterColumns)      */
/* ------------------------------------------------------------------ */

interface CreateColumnsOptions {
  matterOptions: { label: string; value: string }[];
  firmOptions: { label: string; value: string }[];
}

function createCrossMatterChangesColumns({
  matterOptions,
  firmOptions,
}: CreateColumnsOptions): TableColumn<MatterChangeSummarySchema>[] {
  return [
    {
      id: 'matter_name',
      header: 'Matter',
      accessorFn: (row) => row.matter_name,
      cell: renderMatterCell,
      sortable: true,
      sort: {
        sortKey: 'name',
        labels: { asc: 'A to Z', desc: 'Z to A' },
      },
      filter: {
        type: 'multiselect',
        options: matterOptions,
        searchable: true,
        placeholder: 'Filter by matter',
      },
      width: '280px',
      truncate: false,
    },
    {
      id: 'firm_name',
      header: 'Firm',
      accessorFn: (row) => row.firm_name,
      sortable: true,
      sort: {
        sortKey: 'firm__name',
        labels: { asc: 'A to Z', desc: 'Z to A' },
      },
      filter: {
        type: 'multiselect',
        options: firmOptions,
        searchable: true,
        placeholder: 'Filter by firm',
      },
      width: '200px',
    },
    {
      id: 'total_events',
      header: 'Pending',
      accessorFn: (row) => row.total_events,
      cell: renderPendingCell,
      sortable: true,
      sort: {
        labels: { asc: 'Fewest first', desc: 'Most first' },
      },
      width: '100px',
      align: 'center',
    },
    {
      id: 'categories',
      header: 'Categories',
      accessorFn: (row) =>
        row.category_counts
          .filter((c) => c.count > 0)
          .map((c) => c.category)
          .join(', '),
      cell: renderCategoriesCell,
      truncate: false,
    },
    {
      id: 'latest_event_at',
      header: 'Latest Event',
      accessorFn: (row) => row.latest_event_at,
      cell: renderLatestEventCell,
      sortable: true,
      sort: {
        labels: { asc: 'Oldest first', desc: 'Newest first' },
      },
      width: '140px',
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  Page component                                                    */
/* ------------------------------------------------------------------ */

export const CrossMatterChangesListPage = () => {
  const navigate = useNavigate();

  // Pagination / sorting / filtering state (URL-synced)
  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 50,
    defaultSort: '-latest_event_at',
  });

  // Build query params — map `search` → `matter_name` for backend
  const queryParams = useMemo(() => {
    const base = toQueryParams<CrossMatterChangesFilterParams>(state);
    // The backend expects `matter_name`, not `search`
    if (base.search) {
      base.matter_name = base.search;
      delete base.search;
    }
    return base;
  }, [state]);

  // Data fetching
  const { data, isLoading } = useGetCrossMatterChangesListQuery(queryParams);
  const matters = useMemo(() => getResults(data), [data]);
  const totalCount = getTotalCount(data);

  // Filter options (distinct matters + firms with pending changes)
  const { data: filterOptionsData } =
    useGetCrossMatterChangesFilterOptionsQuery();

  const matterOptions = useMemo(
    () =>
      (filterOptionsData?.matters ?? []).map((m) => ({
        label: m.name,
        value: m.id,
      })),
    [filterOptionsData?.matters],
  );

  const firmOptions = useMemo(
    () =>
      (filterOptionsData?.firms ?? []).map((f) => ({
        label: f.name,
        value: f.id,
      })),
    [filterOptionsData?.firms],
  );

  // Column definitions
  const columns = useMemo(
    () => createCrossMatterChangesColumns({ matterOptions, firmOptions }),
    [matterOptions, firmOptions],
  );

  // Handlers
  const handleRowClick = (row: MatterChangeSummarySchema) =>
    navigate(`/cross-matter-changes/${row.matter_id}`);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    const backendKey = getBackendFilterKey(columnId);
    const newFilters = { ...state.filters };

    if (value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[backendKey];
    } else {
      newFilters[backendKey] = value;
    }

    actions.setFilters(newFilters);
  };

  const handleSortChange = (columnId: string, direction?: 'asc' | 'desc') => {
    const orderingField = getOrderingField(columnId);
    actions.setOrdering(orderingField, direction);
  };

  const currentSortField = useMemo(
    () => state.ordering.replace(/^-/, ''),
    [state.ordering],
  );

  const filterValues = useMemo(() => {
    const result: Record<string, string | string[] | null> = {};
    Object.entries(state.filters).forEach(([backendKey, value]) => {
      const columnId = getColumnIdFromBackend(backendKey);
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[columnId] = String(value);
      } else {
        result[columnId] = value;
      }
    });
    return result;
  }, [state.filters]);

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-2 overflow-hidden bg-gray-50 px-10 py-2 dark:bg-zinc-950">
      <div className="flex flex-shrink-0 items-center justify-between">
        <h1 className="font-serif text-4xl font-medium text-black dark:text-white">
          Cross-Matter Changes
        </h1>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <DataTable
          data={matters}
          columns={columns}
          getRowKey={(row) => row.matter_id}
          searchEnabled
          searchPlaceholder="Search by matter name"
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          disableClientFilter
          paginationEnabled
          currentPage={actions.getCurrentPage()}
          totalPages={actions.getTotalPages(totalCount)}
          totalCount={totalCount}
          pageSize={state.limit}
          onPageChange={actions.setPage}
          onPageSizeChange={actions.setLimit}
          onSortChange={handleSortChange}
          sortField={currentSortField}
          sortDirection={state.ordering.startsWith('-') ? 'desc' : 'asc'}
          onFilterChange={handleFilterChange}
          filterValues={filterValues}
          isLoading={isLoading}
          emptyMessage="No matters with pending changes"
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
};
